import React from 'react';
import { Trophy, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useLocale } from '../contexts/LocaleContext';
import { AngleUnderline } from './Landing/angle-underline';
import { cn } from '../lib/utils';
import { ScribaneMark } from './ScribaneMark';

export const CHAPTER_ZERO_OVERLAY_Z_INDEX = 10050;

export type ChapterZeroToastKind = 'achievement' | 'reward';

export type ChapterZeroToastItem = {
  kind: ChapterZeroToastKind;
  text: string;
};

type ChapterZeroAchievementToastProps = {
  items: ChapterZeroToastItem[];
  onClose: () => void;
};

const dmMonoStyle: React.CSSProperties = {
  fontFamily: "'DM Mono', monospace",
};

const ToastGlyph: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--scribane-green)] text-[var(--scribane-black)]">
    {children}
  </span>
);

export const ChapterZeroAchievementToast: React.FC<ChapterZeroAchievementToastProps> = ({
  items,
  onClose,
}) => {
  const { t } = useLocale();
  const [isVisible, setIsVisible] = React.useState(false);
  const hasAchievement = items.some((item) => item.kind === 'achievement');
  const hasReward = items.some((item) => item.kind === 'reward');

  React.useEffect(() => {
    const showTimer = window.setTimeout(() => setIsVisible(true), 10);
    return () => window.clearTimeout(showTimer);
  }, []);

  return createPortal(
    <div
      className={cn(
        'fixed right-4 bottom-4 w-[min(22rem,calc(100vw-2rem))] rounded-md border border-border bg-popover p-4 text-popover-foreground shadow-lg',
        'transition-all duration-300 ease-out'
      )}
      style={{
        zIndex: CHAPTER_ZERO_OVERLAY_Z_INDEX,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(10px)',
      }}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <span className="flex shrink-0 items-start gap-1">
          {hasAchievement && (
            <ToastGlyph>
              <Trophy className="h-4 w-4" strokeWidth={2} aria-hidden="true" />
            </ToastGlyph>
          )}
          {hasReward && (
            <ToastGlyph>
              <ScribaneMark className="h-5 w-5" />
            </ToastGlyph>
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p
            className="relative inline-block text-xs font-normal uppercase tracking-[0.12em] pb-2"
            style={dmMonoStyle}
          >
            <span className="relative z-10">{t('onboarding_title')}</span>
            <AngleUnderline
              size="thin"
              className="left-[0.04em] right-[0.04em] -bottom-0.5 h-2"
            />
          </p>
          <ul className="mt-2 flex flex-col gap-1.5 text-sm text-foreground">
            {items.map((item, index) => (
              <li key={`${item.kind}-${index}-${item.text}`}>{item.text}</li>
            ))}
          </ul>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-sm p-1 text-muted-foreground hover:text-foreground"
          aria-label={t('close')}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>,
    document.body
  );
};
